/**
 * Category-specific fallback images for products without photos.
 *
 * IMPORTANT: each category MUST have a UNIQUE Unsplash URL. Previously,
 * many keys pointed to the same photo, causing every "Featured" card on
 * the homepage to show the exact same image of orange-vested workers.
 *
 * All images are 800×450 (16:9), q=80 WebP-friendly via Unsplash CDN.
 * License: Unsplash Free (https://unsplash.com/license)
 */

// --- Atomic image library (each URL appears once) ---
const IMG = {
  // Drilling rigs / forage
  drillingRig: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=450&fit=crop&q=80',
  drillingRig2: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=800&h=450&fit=crop&q=80',

  // Soil sampling / sondage
  soilSampling: 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?w=800&h=450&fit=crop&q=80',

  // Concrete pumps / pompe à béton
  concretePump: 'https://images.unsplash.com/photo-1597857166473-86a36ce4ce41?w=800&h=450&fit=crop&q=80',

  // Hydraulic / flexible / hydraulique
  hydraulicHose: 'https://images.unsplash.com/photo-1581092580497-e0d23cbdf1dc?w=800&h=450&fit=crop&q=80',

  // Concrete spraying / projection béton / shotcrete
  shotcrete: 'https://images.unsplash.com/photo-1503387837-b154d5074bd2?w=800&h=450&fit=crop&q=80',

  // Grouting / injection
  grouting: 'https://images.unsplash.com/photo-1542621334-a254cf47733d?w=800&h=450&fit=crop&q=80',

  // Pressuremeter / instruments / mesure
  instrumentation: 'https://images.unsplash.com/photo-1581092335397-9583eb92d232?w=800&h=450&fit=crop&q=80',
  sensor: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=800&h=450&fit=crop&q=80',

  // Workshop / repair / atelier
  workshop: 'https://images.unsplash.com/photo-1530124566582-a618bc2615dc?w=800&h=450&fit=crop&q=80',

  // Excavator / pelle
  excavator: 'https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=450&fit=crop&q=80',

  // Tower crane / grue
  crane: 'https://images.unsplash.com/photo-1517089596392-fb9a9033e05b?w=800&h=450&fit=crop&q=80',

  // Construction site (generic BTP)
  constructionSite: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=450&fit=crop&q=80',

  // Engineer with plans / études géotechniques / bureau
  engineerOffice: 'https://images.unsplash.com/photo-1581090700227-1e37b190418e?w=800&h=450&fit=crop&q=80',

  // Worker/technician on site / talents
  technician: 'https://images.unsplash.com/photo-1581092446327-9b52bd1570c2?w=800&h=450&fit=crop&q=80',
  team: 'https://images.unsplash.com/photo-1521791136064-7986c2920216?w=800&h=450&fit=crop&q=80',

  // Software / laptop / logiciel
  software: 'https://images.unsplash.com/photo-1517694712202-14dd9538aa97?w=800&h=450&fit=crop&q=80',

  // Used equipment / occasion
  usedEquipment: 'https://images.unsplash.com/photo-1530855160224-fc8b18d68458?w=800&h=450&fit=crop&q=80',

  // Mixing plant / centrale
  mixingPlant: 'https://images.unsplash.com/photo-1565008576549-57569a49371d?w=800&h=450&fit=crop&q=80',
};

// --- Keyword → image mapping ---
// Order matters: more specific keywords first.
const CATEGORY_IMAGES = {
  // ---- Drilling / Forage / Sondage ----
  pressiomètre: IMG.instrumentation,
  pressiometer: IMG.instrumentation,
  'dialog mx': IMG.instrumentation,
  dialog: IMG.instrumentation,

  forage: IMG.drillingRig,
  foreuse: IMG.drillingRig,
  drilling: IMG.drillingRig,
  drill: IMG.drillingRig,
  bap: IMG.drillingRig2,

  sondage: IMG.soilSampling,
  sonde: IMG.soilSampling,
  carottage: IMG.soilSampling,
  sampling: IMG.soilSampling,

  // ---- Hydraulics / Pumps / Injection ----
  'flexible hydro': IMG.hydraulicHose,
  flexible: IMG.hydraulicHose,
  hydraulique: IMG.hydraulicHose,
  hydraulic: IMG.hydraulicHose,

  'pompe à béton': IMG.concretePump,
  'pompe a beton': IMG.concretePump,
  'concrete pump': IMG.concretePump,
  pompe: IMG.concretePump,
  pump: IMG.concretePump,

  injection: IMG.grouting,
  grouting: IMG.grouting,

  'projection béton': IMG.shotcrete,
  shotcrete: IMG.shotcrete,
  projection: IMG.shotcrete,

  // ---- Instrumentation / Sensors ----
  instrumentation: IMG.instrumentation,
  mesure: IMG.instrumentation,
  capteur: IMG.sensor,
  sensor: IMG.sensor,

  // ---- BTP heavy equipment ----
  pelle: IMG.excavator,
  excavator: IMG.excavator,
  excavatrice: IMG.excavator,

  grue: IMG.crane,
  crane: IMG.crane,

  centrale: IMG.mixingPlant,
  'mixing plant': IMG.mixingPlant,

  btp: IMG.constructionSite,
  chantier: IMG.constructionSite,
  construction: IMG.constructionSite,

  // ---- Repair / Maintenance ----
  réparation: IMG.workshop,
  reparation: IMG.workshop,
  repair: IMG.workshop,
  maintenance: IMG.workshop,
  atelier: IMG.workshop,
  workshop: IMG.workshop,

  // ---- Engineering studies ----
  'étude géotechnique': IMG.engineerOffice,
  'etude geotechnique': IMG.engineerOffice,
  étude: IMG.engineerOffice,
  etude: IMG.engineerOffice,
  study: IMG.engineerOffice,
  ingénierie: IMG.engineerOffice,
  bureau: IMG.engineerOffice,
  rapport: IMG.engineerOffice,

  // ---- Talents / Workers ----
  technicien: IMG.technician,
  technician: IMG.technician,
  opérateur: IMG.technician,
  operateur: IMG.technician,
  operator: IMG.technician,
  hydraulicien: IMG.technician,
  mécanicien: IMG.technician,
  mecanicien: IMG.technician,
  lutz: IMG.technician,

  équipe: IMG.team,
  equipe: IMG.team,
  team: IMG.team,
  service: IMG.team,

  // ---- Software ----
  logiciel: IMG.software,
  software: IMG.software,
  application: IMG.software,
  app: IMG.software,

  // ---- Used / Occasion ----
  occasion: IMG.usedEquipment,
  used: IMG.usedEquipment,
  'matériel d\'occasion': IMG.usedEquipment,
};

// Variety pool used when no keyword matches.
// Each fallback is DIFFERENT to avoid all cards looking identical.
const DEFAULT_IMAGES = [
  IMG.constructionSite,
  IMG.drillingRig,
  IMG.engineerOffice,
  IMG.workshop,
  IMG.team,
  IMG.excavator,
];

/**
 * Get the appropriate fallback image URL based on product name.
 *
 * Algorithm:
 *   1. Lowercase the product name.
 *   2. Find the FIRST matching keyword (longest match wins via key order).
 *   3. If no match: hash the name and pick a varied default.
 *
 * @param {string} productName
 * @param {number} [index=0] Optional index for forced variety.
 * @returns {string} Unsplash URL (16:9, 800×450).
 */
export function getFallbackImage(productName, index = 0) {
  if (!productName) {
    return DEFAULT_IMAGES[index % DEFAULT_IMAGES.length];
  }

  const nameLower = productName.toLowerCase();

  // Iterate keys: longest first, so "pompe à béton" matches before "pompe"
  const sortedKeys = Object.keys(CATEGORY_IMAGES).sort(
    (a, b) => b.length - a.length
  );

  for (const keyword of sortedKeys) {
    if (nameLower.includes(keyword)) {
      return CATEGORY_IMAGES[keyword];
    }
  }

  // Stable hash → same product always gets the same default (no flicker)
  const hash = nameLower
    .split('')
    .reduce((acc, char) => acc + char.charCodeAt(0), 0);
  return DEFAULT_IMAGES[hash % DEFAULT_IMAGES.length];
}

/**
 * Get fallback image based on category ID/name.
 * @param {string} categoryId
 * @param {string} [categoryName='']
 * @returns {string}
 */
export function getFallbackImageByCategory(categoryId, categoryName = '') {
  return getFallbackImage(categoryName || categoryId || '');
}

/**
 * Normalize image URL to handle different formats:
 * - Full URLs (http/https) → use as-is
 * - Relative URLs starting with / → prepend API base
 * - Relative URLs without / → prepend API base with /
 * 
 * @param {string} imageUrl - The image URL to normalize
 * @param {string} apiBase - The API base URL (e.g., process.env.REACT_APP_BACKEND_URL)
 * @returns {string|null} The normalized image URL or null if invalid
 */
export function normalizeImageUrl(imageUrl, apiBase) {
  if (!imageUrl || typeof imageUrl !== 'string' || imageUrl.trim() === '') {
    return null;
  }
  
  const trimmedUrl = imageUrl.trim();
  
  // Already a full URL
  if (trimmedUrl.startsWith('http://') || trimmedUrl.startsWith('https://')) {
    return trimmedUrl;
  }
  
  // Relative URL starting with /
  if (trimmedUrl.startsWith('/')) {
    return `${apiBase}${trimmedUrl}`;
  }
  
  // Relative URL without leading /
  return `${apiBase}/${trimmedUrl}`;
}

/**
 * Get the first valid image URL from an images array.
 * 
 * @param {Array} images - Array of image URLs
 * @param {string} apiBase - The API base URL
 * @param {string} fallback - Fallback image URL if no valid image found
 * @returns {string} The image URL to use
 */
export function getFirstValidImageUrl(images, apiBase, fallback) {
  if (!images || !Array.isArray(images)) {
    return fallback;
  }
  
  const validImages = images.filter(img => img && typeof img === 'string' && img.trim() !== '');
  
  if (validImages.length === 0) {
    return fallback;
  }
  
  return normalizeImageUrl(validImages[0], apiBase) || fallback;
}

export default { getFallbackImage, getFallbackImageByCategory, normalizeImageUrl, getFirstValidImageUrl };
